import React, { useEffect, useMemo } from 'react';

import Cmip6ExperimentHelp from '../../common/Cmip6ExperimentHelp';
import Selects from '../../lib/Selects';
import { PartialTr } from '../../lib/types';
import { useAppDispatch as useDispatch, useAppSelector as useSelector } from '../../store';
import { useGetAvailableVariablesQuery, useGetDatabaseConfigQuery, useGetDatabaseEnsemblesQuery, useGetDatabaseExperimentsQuery, useGetDatabaseModelsQuery, useGetDatabaseVariablesQuery, useGetKwargsQuery } from '../api/apiSlice';
import { selectDatabase, selectEnsemble, selectExperiment, selectFrequency, selectModel, selectSingleVariable, updateDatabase, updateEnsemble, updateExperiment, updateModel, updateVariables } from './fileRepertoireSlice';
import styles from './SingleVariableSelector.scss';

interface VariablesProps {
  selectsClassName: string;
}
const Variables = ({selectsClassName}: VariablesProps) => {
  const dispatch = useDispatch();

  const database = useSelector(selectDatabase);
  const ensemble = useSelector(selectEnsemble);
  const experiment = useSelector(selectExperiment);
  const frequency = useSelector(selectFrequency);
  const model = useSelector(selectModel);
  const variable = useSelector(selectSingleVariable);

  const items = (() => {
    const { data: config } = useGetDatabaseConfigQuery();
    const { data: variables } = useGetDatabaseVariablesQuery();
    const { data: availableVariables } = useGetAvailableVariablesQuery(
      config?.data_complete ? {database, all: true} : {database, model, ensemble, experiment, frequency, all: false}
    );

    const availableVariablesSet = useMemo(() => {
      return new Set(availableVariables);
    }, [availableVariables]);

    return useMemo(() => {
      return variables.filter(variable => availableVariablesSet.has(variable.variable_name));
    }, [variables, availableVariablesSet]);
  })();

  useEffect(() => {
    if (items.length < 1) {
      dispatch(updateVariables({variables: []}));
    } else if (!items.some(item => item.variable_name === variable)) {
      dispatch(updateVariables({variables: [items[0].variable_name]}));
    }
  }, [items]);

  if (items == null) {
    return null;
  }

  return (
    <Selects
      className={selectsClassName}
      handleChange={variable => dispatch(updateVariables({variables: [variable]}))}
      items={items}
      labelFormat={'{variable_name} ({variable_longname})'}
      value={variable}
      valueFormat={'{variable_name}'}
    />
  );
};

interface SingleVariableSelectorProps {
  controlClassName?: string;
  tr: PartialTr,
}
const SingleVariableSelector = ({controlClassName, tr}: SingleVariableSelectorProps) => {
  const dispatch = useDispatch();

  const { data: ensembles } = useGetDatabaseEnsemblesQuery();
  const { data: experiments } = useGetDatabaseExperimentsQuery();
  const { data: models } = useGetDatabaseModelsQuery();

  const database = useSelector(selectDatabase);
  const ensemble = useSelector(selectEnsemble);
  const experiment = useSelector(selectExperiment);
  const model = useSelector(selectModel);

  const { data: kwargs } = useGetKwargsQuery(window.location.href);
  useEffect(() => {
    const database = kwargs?.climate_database as string;
    dispatch(updateDatabase({database}));
  }, [kwargs]);

  useEffect(() => {
    if (ensemble == null && ensembles.length > 0) {
      dispatch(updateEnsemble({ensemble: ensembles[0].ensemble_shortname}));
    }
  }, [ensembles]);

  useEffect(() => {
    if (experiment == null && experiments.length > 0) {
      dispatch(updateExperiment({experiment: experiments[0].experiment_shortname}));
    }
  }, [experiments]);

  useEffect(() => {
    if (model == null && models.length > 0) {
      dispatch(updateModel({model: models[0].model_shortname}));
    }
  }, [models]);

  const selectsClassName = controlClassName ?? styles.control;

  return (
    <React.Fragment>
      <div>{tr('Dataset', 'データセット')}</div>
      <Selects
        className={selectsClassName}
        handleChange={model => dispatch(updateModel({model}))}
        items={models}
        labelFormat={'{model_name}/{model_shortname} ({calendar})'}
        value={model}
        valueFormat={'{model_shortname}'}
      />
      <div>{tr('Ensemble', 'アンサンブル')}{database === 'cmip6' && <Cmip6ExperimentHelp tr={tr} />}</div>
      <Selects
        className={selectsClassName}
        handleChange={ensemble => dispatch(updateEnsemble({ensemble}))}
        items={ensembles}
        labelFormat={'{ensemble_name}'}
        value={ensemble}
        valueFormat={'{ensemble_shortname}'}
      />
      <div>{tr('Scenario', 'シナリオ')}</div>
      <Selects
        className={selectsClassName}
        handleChange={experiment => dispatch(updateExperiment({experiment}))}
        items={experiments}
        labelFormat={'{experiment_name}'}
        value={experiment}
        valueFormat={'{experiment_shortname}'}
      />
      <div>{tr('Variable', '変数')}</div>
      <Variables selectsClassName={selectsClassName} />
    </React.Fragment>
  );
};

export default SingleVariableSelector;
